import { type RefObject, useEffect } from 'react';

type UseClickOutsideParams = {
  refs: ReadonlyArray<RefObject<HTMLElement>>;
  enabled?: boolean;
  onOutsideClick: () => void;
};

export function useClickOutside({
  refs,
  enabled = true,
  onOutsideClick,
}: UseClickOutsideParams) {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleDocumentPointerDown = (event: Event) => {
      const target = event.target as Node | null;
      if (!target) {
        return;
      }

      const isInsideKnownElement = refs.some(ref =>
        ref.current?.contains(target),
      );

      if (!isInsideKnownElement) {
        onOutsideClick();
      }
    };

    if (typeof window !== 'undefined' && 'PointerEvent' in window) {
      document.addEventListener('pointerdown', handleDocumentPointerDown);

      return () => {
        document.removeEventListener('pointerdown', handleDocumentPointerDown);
      };
    }

    document.addEventListener('mousedown', handleDocumentPointerDown);
    document.addEventListener('touchstart', handleDocumentPointerDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentPointerDown);
      document.removeEventListener('touchstart', handleDocumentPointerDown);
    };
  }, [enabled, onOutsideClick, refs]);
}
